import { computed } from 'vue';
import store from '@/store';

/**
 * IDs de teatros donde los gastos de gestión (GG) se anulan automáticamente
 * cuando el descuento (voucher) cubre el 100% del precio base de la entrada.
 *
 * En estos teatros, si el descuento deja el precio base en 0, los GG también
 * van a 0 y se muestra el botón de descarga de entradas en lugar del de compra.
 */
const ZERO_GG_THEATER_IDS = [1, 10, 791];

// ─── FUENTE ÚNICA DE VERDAD ────────────────────────────────────────────────────

/**
 * Calcula el resumen económico completo de una reserva como función pura.
 *
 * Unifica todos los modos (con recinto, sin recinto precio único, sin recinto
 * multi-precio) en un contrato simétrico. Es la ÚNICA fuente de verdad para
 * pricing: gastos, descuentos y total siempre derivan de aquí.
 *
 * @param {object} input
 * @param {boolean}  input.esMultiPrecio          - modo multi-precio activo
 * @param {boolean}  input.esUnidades             - modo precio único genérico activo
 * @param {number}   input.subtotalBase           - subtotal bruto de entradas/unidades (sin management)
 * @param {number}   input.managementExterno      - total de gastos de gestión externos
 * @param {boolean}  input.hasInternalManagement  - flag: el gd ya está incluido en precio
 * @param {number}   input.discount               - total del descuento por voucher (sobre base)
 * @param {number}   input.productosTotal         - total de productos (ya incluye su propio management)
 * @param {number}   input.segurosTotal           - total de seguros seleccionados
 * @param {Array}    input.lines                  - líneas de desglose (multi-precio)
 *
 * @returns {{ base: number, management: number, discount: number, total: number, lines: Array, hasInternalManagement: boolean }}
 */
export function calculateReservaSummary({
    esMultiPrecio = false,
    esUnidades = false,
    subtotalBase = 0,
    managementExterno = 0,
    hasInternalManagement = false,
    discount = 0,
    productosTotal = 0,
    segurosTotal = 0,
    lines = [],
}) {
    const base = Math.max(0, subtotalBase - discount);

    // Para con-recinto: el management ya viene en precio_total cuando es externo.
    // Para sin-recinto (genérico / multi-precio): debemos sumarlo explícitamente
    // sólo si es externo (no interno).
    const management = hasInternalManagement ? 0 : managementExterno;

    const total = Math.max(0, base + management + productosTotal + segurosTotal);

    return {
        base,
        management,
        discount,
        total,
        lines,
        hasInternalManagement,
    };
}

// ─── HELPERS INTERNOS ─────────────────────────────────────────────────────────

/**
 * Calcula el management externo de un listado de precios de reserva
 * (sin recinto, genérico o multi-precio) ponderado por las cantidades
 * solicitadas. Retorna { total, hasInternalManagement }.
 *
 * Para modo precio único pasa cantidades = [{ precio_id, cantidad: n }] con un solo item.
 * Para modo multi-precio itera todas las líneas.
 *
 * @param {Array} precios_reserva    - array de definiciones de precio del backend
 * @param {Array} cantidades         - array de { precio_id, cantidad }
 * @returns {{ total: number, hasInternalManagement: boolean }}
 */
function calcularManagementReservaGenerica(precios_reserva, cantidades) {
    if (!precios_reserva || !precios_reserva.length || !cantidades || !cantidades.length) {
        return { total: 0, hasInternalManagement: false };
    }

    let total = 0;
    let algunoExterno = false;
    let algunoInterno = false;

    for (const c of cantidades) {
        if (!c.cantidad || c.cantidad <= 0) continue;
        const precioDef = precios_reserva.find(p => p.id === c.precio_id);
        if (!precioDef) continue;

        const esInterno = !!(
            precioDef.gd_interno ||
            precioDef.price_management_online_interno ||
            precioDef.price_management_interno
        );
        if (esInterno) {
            algunoInterno = true;
        } else {
            algunoExterno = true;
            const gd = parseFloat(
                precioDef.gd ??
                precioDef.price_management_online ??
                precioDef.price_management ??
                0
            );
            total += gd * c.cantidad;
        }
    }

    // Si hay mezcla de interno/externo, tratamos como externo para no ocultar gastos.
    // hasInternalManagement=true solo cuando TODOS son internos.
    const hasInternalManagement = algunoInterno && !algunoExterno;

    return { total: hasInternalManagement ? 0 : total, hasInternalManagement };
}

// ─── COMPOSABLE ────────────────────────────────────────────────────────────────

/**
 * Composable de dominio para el cálculo de precios en el resumen de compra.
 *
 * Encapsula:
 *  - Detección de tipo de reserva (unidades genéricas vs multi-precio)
 *  - Líneas de desglose multi-precio
 *  - Subtotales por modo (entradas, plazas, genérico, multi-precio)
 *  - Cálculo del descuento por voucher según modo
 *  - Total final (total_price) con todos los conceptos
 *  - Cálculo de price_management (gastos de gestión)
 *
 * Internamente consume `calculateReservaSummary` como fuente única de verdad
 * para los modos sin recinto (genérico / multi-precio), garantizando simetría
 * entre total mostrado y desglose de gastos.
 *
 * @param {object} props - objeto con las props del componente (puede ser el `props` reactivo de setup)
 * @param {Function} calcularTotalProductosFn     - función ya existente en el componente
 * @param {Function} calcularTotalProductosSinManagementFn - ídem
 * @param {import('vue').Ref} segurosSeleccionadosRef - ref con los seguros seleccionados
 * @param {import('vue').Ref|null} [unitsTotalRef=null] - ref con el total de unidades de la reserva
 *   (derivado del flujo local, no del store global). Se pasa desde el composable padre que
 *   ya posee el dato (e.g. useReservaFlow → unitsTotal). Si es null, `unidades_reserva_total`
 *   devuelve 0, lo que es seguro para todos los modos de cálculo actuales donde ese valor
 *   sólo se expone informativamente.
 */
export function useResumenPricing(props, calcularTotalProductosFn, calcularTotalProductosSinManagementFn, segurosSeleccionadosRef, unitsTotalRef = null) {
    // ── MODO DE RESERVA ───────────────────────────────────────────────────────

    const tieneLineasReales = computed(() => {
        const tieneEntradas = Array.isArray(props.entradas) && props.entradas.length > 0;
        const tieneEntradasPlazas = Array.isArray(props.entradasPlazas) && props.entradasPlazas.length > 0;

        return tieneEntradas || tieneEntradasPlazas;
    });

    /**
     * True cuando se recibió cantidad_solicitada > 0 (reserva de unidades genéricas, precio único).
     */
    const es_reserva_unidades = computed(() => {
        return !!props.cantidad_solicitada && props.cantidad_solicitada > 0 && !tieneLineasReales.value;
    });

    /**
     * True cuando se recibió cantidades_por_precio + precios_reserva (multi-precio).
     */
    const es_reserva_multi_precio = computed(() => {
        return (
            Array.isArray(props.cantidades_por_precio) &&
            props.cantidades_por_precio.length > 0 &&
            Array.isArray(props.precios_reserva) &&
            props.precios_reserva.length > 0
        );
    });

    // ── DESGLOSE MULTI-PRECIO ─────────────────────────────────────────────────

    /**
     * Líneas de desglose del multi-precio: [{ precio_id, nombre, precio_unitario, cantidad, subtotal }].
     */
    const lineas_multi_precio = computed(() => {
        if (!es_reserva_multi_precio.value) return [];
        return props.cantidades_por_precio
            .filter(cp => cp.cantidad_solicitada > 0)
            .map(cp => {
                const precioDef = props.precios_reserva.find(p => p.id === cp.precio_id);
                const precioUnitario = parseFloat(precioDef?.precio ?? 0);
                return {
                    precio_id: cp.precio_id,
                    nombre: precioDef?.nombre ?? `Precio ${cp.precio_id}`,
                    precio_unitario: precioUnitario,
                    cantidad: cp.cantidad_solicitada,
                    subtotal: precioUnitario * cp.cantidad_solicitada,
                };
            });
    });

    /**
     * Subtotal bruto multi-precio: suma de (precio_unitario × cantidad).
     */
    const subtotal_multi_precio = computed(() => {
        if (!es_reserva_multi_precio.value) return 0;
        return lineas_multi_precio.value.reduce((sum, l) => sum + l.subtotal, 0);
    });

    // ── PRECIO UNITARIO Y SUBTOTAL (precio único) ─────────────────────────────

    /**
     * Precio unitario de la reserva en modo precio único.
     * Prioriza el primer asiento de `entradas`; si no hay, usa el primer precio de precios_reserva.
     */
    const precio_unitario_reserva = computed(() => {
        if (!es_reserva_unidades.value) return 0;
        const fromEntradas = parseFloat(props.entradas?.[0]?.precio?.precio ?? 0);
        if (fromEntradas > 0) return fromEntradas;
        return parseFloat(props.precios_reserva?.[0]?.precio ?? 0);
    });

    /**
     * Unidades totales de la reserva.
     * Deriva del parámetro `unitsTotalRef` pasado explícitamente por el llamante
     * (que lo obtiene de `useReservaFlow → unitsTotal`). Devuelve 0 si no se pasa.
     */
    const unidades_reserva_total = computed(() => {
        return unitsTotalRef?.value ?? 0;
    });

    /**
     * Subtotal bruto precio único: precio_unitario × cantidad_solicitada.
     */
    const subtotal_reserva_unidades = computed(() => {
        return precio_unitario_reserva.value * props.cantidad_solicitada;
    });

    // ── PRICE MANAGEMENT INTERNO ──────────────────────────────────────────────

    /**
     * Valor de gd_interno (gestión interna) para el resumen.
     * Se resuelve con prioridad: entradas → plazas → precios_reserva.
     *
     * Para multi-precio: true solo si TODAS las líneas activas tienen gestión interna.
     * Para precio único genérico: lee precios_reserva[0].gd_interno.
     */
    const price_management_interno_resumen = computed(() => {
        if (props.entradas && props.entradas.length > 0) {
            return props.entradas[0].precio.gd_interno;
        }
        if (props.entradasPlazas && props.entradasPlazas.length > 0) {
            return props.entradasPlazas[0].price_management_interno;
        }
        if (props.precios_reserva && props.precios_reserva.length > 0) {
            // Para multi-precio: usa el resultado del cálculo unificado de management
            if (es_reserva_multi_precio.value) {
                const cantidades = lineas_multi_precio.value.map(l => ({
                    precio_id: l.precio_id,
                    cantidad: l.cantidad,
                }));
                return calcularManagementReservaGenerica(props.precios_reserva, cantidades).hasInternalManagement ? 1 : 0;
            }
            return props.precios_reserva[0].gd_interno ?? 0;
        }
        return 0;
    });

    // ── TEATROS CON GG CERO POR DESCUENTO TOTAL ──────────────────────────────

    /**
     * True cuando el teatro es uno de los especiales (1, 10, 791) y el precio
     * base después del descuento es ≤ 0. En ese caso, los gastos de gestión (GG)
     * deben ir a 0 también, y se habilita la descarga directa de entradas.
     *
     * Cubre los cuatro modos: con recinto (entradas), plazas, multi-precio y
     * precio único genérico.
     *
     * Para plazas, el descuento se aplica desde el backend (el frontend no calcula
     * voucher para plazas), por lo que la detección se basa en si el precio base
     * de las plazas ya es ≤ 0.
     */
    const forceZeroManagement = computed(() => {
        // El objeto teatro completo viene del settings/config del backend (FrontEventsModule).
        // Usamos su id numérico para identificar los teatros 1, 10, 791.
        const teatroId = Number(store.getters.getTeatro?.id);
        if (!ZERO_GG_THEATER_IDS.includes(teatroId)) return false;

        // Con recinto (entradas): precio base total - descuento aplicado
        if (props.entradas && props.entradas.length > 0) {
            return (total.value - voucher_discounted_total.value) <= 0;
        }
        // Plazas (con recinto): el descuento lo aplica el backend,
        // verificamos si el precio base ya quedó en 0
        if (props.entradasPlazas && props.entradasPlazas.length > 0) {
            return totalPlazas.value <= 0;
        }
        // Multi-precio (sin recinto)
        if (es_reserva_multi_precio.value) {
            return (subtotal_multi_precio.value - voucher_discounted_total.value) <= 0;
        }
        // Precio único genérico (sin recinto)
        if (es_reserva_unidades.value) {
            return (subtotal_reserva_unidades.value - voucher_discounted_total.value) <= 0;
        }
        return false;
    });

    // ── TOTALES (entradas con recinto) ────────────────────────────────────────

    const total_con_gd = computed(() => {
        let total = 0;
        for (const entrada of (props.entradas || [])) {
            total += parseFloat(entrada.precio.precio_total);
        }
        return total;
    });

    const total = computed(() => {
        let t = 0;
        for (const entrada of (props.entradas || [])) {
            t += parseFloat(entrada.precio.precio);
        }
        return t;
    });

    const total_gd = computed(() => {
        let t = 0;
        for (const entrada of (props.entradas || [])) {
            t += parseFloat(entrada.precio.gd);
        }
        return t;
    });

    // ── TOTALES (plazas) ──────────────────────────────────────────────────────

    const preciosPlazas = computed(() => {
        if (props.entradasPlazas && props.entradasPlazas.length > 0) {
            return props.entradasPlazas.map(plaza => ({
                ...plaza.precioSeleccionado,
                nombrePlaza: plaza.name,
                sector: plaza.sector,
                fila: plaza.fila,
                butaca: plaza.butaca,
            }));
        }
        return [];
    });

    const totalPlazas = computed(() => {
        if (!props.entradasPlazas || props.entradasPlazas.length === 0) return 0;
        return preciosPlazas.value.reduce((t, e) => t + parseFloat(e.precio ?? 0), 0);
    });

    const totalPlazas_gd = computed(() => {
        if (!props.entradasPlazas || props.entradasPlazas.length === 0) return 0;
        return preciosPlazas.value.reduce((t, e) => t + parseFloat(e.price_management_online ?? 0), 0);
    });

    const totalPlazas_con_gd = computed(() => {
        if (!props.entradasPlazas || props.entradasPlazas.length === 0) return 0;
        return preciosPlazas.value.reduce(
            (t, e) => t + parseFloat(e.precio ?? 0) + parseFloat(e.price_management_online ?? 0),
            0
        );
    });

    // ── SEGUROS ───────────────────────────────────────────────────────────────

    const totalSeguros = computed(() => {
        return (segurosSeleccionadosRef?.value || [])
            .filter(s => s.seguro)
            .reduce((acc, s) => acc + s.importe, 0);
    });

    // ── DESCUENTO VOUCHER ─────────────────────────────────────────────────────

    /**
     * Total descontado por el voucher, calculado según el modo de reserva activo.
     */
    const voucher_discounted_total = computed(() => {
        if (!props.voucher) return 0;

        // Multi-precio
        if (es_reserva_multi_precio.value) {
            const subtotal = subtotal_multi_precio.value;
            if (props.voucher.discount_percent > 0) {
                return (props.voucher.discount_percent / 100) * subtotal;
            }
            const totalUnidades = lineas_multi_precio.value.reduce((sum, l) => sum + l.cantidad, 0);
            const unitsAfectadas = Math.min(totalUnidades, props.voucher.units);
            return parseFloat(props.voucher.discount_fixed) * unitsAfectadas;
        }

        // Precio único genérico
        if (es_reserva_unidades.value) {
            const subtotal = subtotal_reserva_unidades.value;
            if (props.voucher.discount_percent > 0) {
                return (props.voucher.discount_percent / 100) * subtotal;
            }
            const unitsAfectadas = Math.min(props.cantidad_solicitada, props.voucher.units);
            return parseFloat(props.voucher.discount_fixed) * unitsAfectadas;
        }

        // Con recinto (entradas)
        let descuento = 0;
        let voucherUnits = props.voucher.units;
        for (const entrada of (props.entradas || [])) {
            if (voucherUnits <= 0) break;
            voucherUnits--;
            if (props.voucher.discount_percent > 0) {
                descuento += (props.voucher.discount_percent / 100) * parseFloat(entrada.precio.precio);
            } else {
                descuento += parseFloat(props.voucher.discount_fixed);
            }
        }
        return descuento;
    });

    // ── GASTOS DE GESTIÓN ─────────────────────────────────────────────────────

    /**
     * Gastos de gestión externos totales.
     *
     * Ahora unifica los tres modos:
     *  - Con recinto: total_gd (de entradas) + totalPlazas_gd
     *  - Sin recinto genérico / multi-precio: calculados desde precios_reserva
     *    vía calcularManagementReservaGenerica (fuente única de verdad)
     *  - Productos: siempre sumados si tienen management externo
     */
    const gastos = computed(() => {
        let gds = 0;

        if (es_reserva_multi_precio.value) {
            // Multi-precio: management de cada tipo de precio × su cantidad
            const cantidades = lineas_multi_precio.value.map(l => ({
                precio_id: l.precio_id,
                cantidad: l.cantidad,
            }));
            const mgmtMulti = calcularManagementReservaGenerica(props.precios_reserva, cantidades).total;
            gds += forceZeroManagement.value ? 0 : mgmtMulti;
        } else if (es_reserva_unidades.value) {
            // Precio único genérico: management del primer precio × cantidad_solicitada
            const primeraDefinicion = props.precios_reserva?.[0];
            if (primeraDefinicion) {
                const cantidades = [{ precio_id: primeraDefinicion.id, cantidad: props.cantidad_solicitada }];
                const mgmtUni = calcularManagementReservaGenerica(props.precios_reserva, cantidades).total;
                gds += forceZeroManagement.value ? 0 : mgmtUni;
            }
        } else {
            // Con recinto (entradas + plazas)
            const gdEntradas = forceZeroManagement.value ? 0 : total_gd.value;
            const gdPlazas = forceZeroManagement.value ? 0 : totalPlazas_gd.value;
            gds += gdEntradas + gdPlazas;
        }

        // Productos: siempre aplican si tienen management externo
        if (props.productos && props.productos.length > 0) {
            gds += props.productos.reduce((t, producto) => {
                const pm = producto.item.price_management_interno === 0
                    ? Number(producto.item.price_management)
                    : 0;
                return t + (pm * producto.units);
            }, 0);
        }

        return gds;
    });

    // ── PRECIO TOTAL FINAL ────────────────────────────────────────────────────

    /**
     * Precio total final que se muestra y cobra al usuario.
     *
     * Para modos sin recinto (genérico / multi-precio) usa calculateReservaSummary
     * como fuente única de verdad, garantizando simetría con `gastos`.
     */
    const total_price = computed(() => {
        const productosTotal = (props.productos && props.productos.length > 0)
            ? calcularTotalProductosFn()
            : 0;

        // Multi-precio
        if (es_reserva_multi_precio.value) {
            const cantidades = lineas_multi_precio.value.map(l => ({
                precio_id: l.precio_id,
                cantidad: l.cantidad,
            }));
            const { total: managementExterno, hasInternalManagement } = calcularManagementReservaGenerica(
                props.precios_reserva,
                cantidades
            );
            const summary = calculateReservaSummary({
                esMultiPrecio: true,
                subtotalBase: subtotal_multi_precio.value,
                managementExterno,
                hasInternalManagement,
                discount: voucher_discounted_total.value,
                productosTotal,
                segurosTotal: 0,
                lines: lineas_multi_precio.value,
            });
            // Teatros especiales (1, 10, 791): si el descuento cubre el 100%
            // del precio base, los GG también van a 0
            if (forceZeroManagement.value) {
                return Math.max(0, summary.base + productosTotal);
            }
            return summary.total;
        }

        // Precio único genérico
        if (es_reserva_unidades.value) {
            const primeraDefinicion = props.precios_reserva?.[0];
            let managementExterno = 0;
            let hasInternalManagement = false;
            if (primeraDefinicion) {
                const cantidades = [{ precio_id: primeraDefinicion.id, cantidad: props.cantidad_solicitada }];
                const mgmt = calcularManagementReservaGenerica(props.precios_reserva, cantidades);
                managementExterno = mgmt.total;
                hasInternalManagement = mgmt.hasInternalManagement;
            }
            const summary = calculateReservaSummary({
                esUnidades: true,
                subtotalBase: subtotal_reserva_unidades.value,
                managementExterno,
                hasInternalManagement,
                discount: voucher_discounted_total.value,
                productosTotal,
                segurosTotal: 0,
                lines: [],
            });
            // Teatros especiales (1, 10, 791): si el descuento cubre el 100%
            // del precio base, los GG también van a 0
            if (forceZeroManagement.value) {
                return Math.max(0, summary.base + productosTotal);
            }
            return summary.total;
        }

        // Con recinto
        let entradas = 0;
        let entradasPlazasTotal = 0;

        if (props.entradas && props.entradas.length > 0) {
            entradas = (props.entradas[0]?.precio.gd_interno
                ? total.value
                : total_con_gd.value) - voucher_discounted_total.value;

            // Teatros especiales (1, 10, 791): si el descuento cubre el 100%
            // del precio base, los GG también van a 0
            if (forceZeroManagement.value) {
                entradas = 0;
            }
        }

        if (props.entradasPlazas && props.entradasPlazas.length > 0) {
            entradasPlazasTotal = preciosPlazas.value[0]?.price_management_interno
                ? totalPlazas.value
                : totalPlazas_con_gd.value;

            // Teatros especiales (1, 10, 791): si el precio base de las plazas
            // quedó en 0 (descuento aplicado por backend), los GG también van a 0
            if (forceZeroManagement.value) {
                entradasPlazasTotal = 0;
            }
        }

        return entradas + entradasPlazasTotal + productosTotal + totalSeguros.value;
    });

    // ── PRECIO PARA ResumenPrecioComponent ────────────────────────────────────

    /**
     * Precio base para el componente ResumenPrecioComponent
     * (sin management, según modo).
     */
    const precio_base_resumen = computed(() => {
        if (es_reserva_multi_precio.value) {
            return subtotal_multi_precio.value - voucher_discounted_total.value;
        }
        if (es_reserva_unidades.value) {
            return subtotal_reserva_unidades.value - voucher_discounted_total.value;
        }
        return (
            total.value +
            totalPlazas.value +
            totalSeguros.value +
            (calcularTotalProductosSinManagementFn ? calcularTotalProductosSinManagementFn() : 0)
        ) - voucher_discounted_total.value;
    });

    return {
        // Modo
        es_reserva_unidades,
        es_reserva_multi_precio,
        // Multi-precio
        lineas_multi_precio,
        subtotal_multi_precio,
        // Precio único genérico
        precio_unitario_reserva,
        unidades_reserva_total,
        subtotal_reserva_unidades,
        // Entradas con recinto
        preciosPlazas,
        total,
        total_con_gd,
        total_gd,
        totalPlazas,
        totalPlazas_gd,
        totalPlazas_con_gd,
        // Gastos y seguros
        gastos,
        totalSeguros,
        price_management_interno_resumen,
        forceZeroManagement,
        // Voucher
        voucher_discounted_total,
        // Total final
        total_price,
        precio_base_resumen,
    };
}
